<template>
	<div class="customer-stories">
		<div class="filters">
			<TransitionGroup name="crossfade" tag="div">
				<SimpleFilter v-if="displaySimpleFilter" key="simple" taxonomy="topics" />
				<MultiFilter v-else key="multi" :taxonomies="['topics', 'industries']" />
			</TransitionGroup>
		</div>

		<main class="is-relative mt-6">
			<TransitionGroup name="crossfade">
				<div v-if="decoratedStories.length > 0" key="stories">
					<TransitionGroup name="grid" class="columns is-multiline" tag="div">
						<div
							v-for="entry in decoratedStories"
							:key="entry.story.slug"
							class="column"
							:class="entry.columnSize"
						>
							<Story :story="entry.story" :column-size="entry.columnSize" />
						</div>
					</TransitionGroup>
				</div>
				<div v-else-if="$store.ready" key="placeholder" class="notification has-text-centered">
					<p>Sorry, no stories match your filters.</p>
				</div>
			</TransitionGroup>
		</main>
	</div>
</template>

<script>

import axios from 'axios';

import errorHandler from 'Utils/errorHandler.js';

import Story from './Story.vue';
import SimpleFilter from './filters/Simple.vue';
import MultiFilter from './filters/Multi.vue';

import { setFilter, getFilteredStories, decorateStoriesWithSlugs } from '../helpers/stories.js';

export default {
	computed: {
		/* Whether or not the simple filter should be displayed */
		displaySimpleFilter() {
			return this.$store.filter.topics.length === 0 && this.$store.filter.industries.length === 0;
		},

		/* List of stories, filtered as per the user's selections */
		filteredStories() {
			return getFilteredStories(this.$store.stories, this.$store.filter.topics, this.$store.filter.industries);
		},

		/*
		 * Stories paired with their column size class. Computed once per render
		 * pass instead of calling a method twice per story (once for the wrapper
		 * column and once for the Story prop).
		 */
		decoratedStories() {
			const noTopicFilter = this.$store.filter.topics.length === 0;

			return this.filteredStories.map((story, index) => ({
				story,
				columnSize: (index % 4 === 0 && noTopicFilter)
					? 'is-half-tablet is-12-desktop'
					: 'is-half-tablet is-one-third-desktop',
			}));
		},
	},

	created() {
		/* Get stories data */
		axios.get(`/data/customers.json`)
			.then((response) => {
				/* Get available topics and industries */
				response.data.forEach(story => {
					/* Get topics */
					if (story.taxonomies?.topic) {
						story.taxonomies.topic.forEach(topic => {
							this.$store.topics[topic.slug] = topic.name;
						});
					}

					/* Get industries */
					if (story.taxonomies?.industry) {
						story.taxonomies.industry.forEach(industry => {
							this.$store.industries[industry.slug] = industry.name;
						});
					}
				});

				/* Apply initial filter, if present and valid */
				if (this.filterTopic && (this.filterTopic in this.$store.topics)) {
					setFilter(this.$store, this.filterTopic, 'topics');
				}

				/* Save stories to the store, decorated with slug Sets for fast filtering */
				this.$store.stories = decorateStoriesWithSlugs(response.data);
				this.$store.ready = true;
			})
			.catch(errorHandler);
	},

	props: {
		/* Pre-set topic filter from Nunjucks */
		filterTopic: {
			type: String,
		},
	},

	components: {
		Story,
		SimpleFilter,
		MultiFilter,
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

.customer-stories {
	min-height: 90vh;
}

.columns > .column {
	transition: all .4s ease;
}

.filters {
	position: relative;
	z-index: 2;
}


.grid-leave-active {
	position: absolute;
}

.grid-enter-active {
	transition-delay: .3s !important;
}

.grid-enter-to {
	transform: scale(1);
	opacity: 1;
}

.grid-enter-from, .grid-leave-to {
	transform: scale(.5);
	opacity: 0;
}

.crossfade-leave-to {
	transform: translateY(-3rem);
	opacity: 0;
}

.crossfade-enter-from {
	transform: translateY(3rem);
	opacity: 0;
}

.crossfade-enter-to {
	transform: translateY(0);
	opacity: 1;
}

.crossfade-leave-active {
	position: absolute;
	transition: transform .2s cubic-bezier(.32, 0, .67, 0), opacity .2s cubic-bezier(.32, 0, .67, 0);
}

.crossfade-enter-active {
	transition: transform .2s cubic-bezier(.33, 1, .68, 1), opacity .2s cubic-bezier(.33, 1, .68, 1);
	transition-delay: .17s;
}

.crossfade-leave-active,
.crossfade-enter-active {
	display: block;
	width: 100%;
}

</style>
